import React from "react";
import { Link } from "react-router-dom";
import { Mail, Instagram } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";

const CONTACT_EMAIL = "founder@meetleo.co.uk";

const accents = [
  "#FFE1C4",
  "#DDE8F8",
  "#D6EEDF",
];

const cards = [
  {
    title: "Questions about Leo",
    body: "Curious how Leo works, what's coming next, or whether it's right for your child? We'd love to chat.",
  },
  {
    title: "Your data & privacy",
    body: (
      <>
        Want to see, change, or delete the data we hold? We'll sort it. See our{" "}
        <Link
          to="/privacy"
          className="underline decoration-[var(--leo-orange)] decoration-2 underline-offset-4 hover:text-[var(--leo-orange-deep)]"
        >
          privacy policy
        </Link>{" "}
        too.
      </>
    ),
  },
  {
    title: "Press & partnerships",
    body: "Working on something we should know about, or want to feature Leo? Drop us a line and let's talk.",
  },
];

export default function Contact() {
  return (
    <main
      data-testid="contact-page"
      className="site min-h-screen pt-[72px]"
    >
      <Nav links={false} />

      {/* Hero */}
      <section className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-8 text-center">
        <span className="eyebrow eyebrow--plain mb-5">Contact · Meet Leo</span>
        <h1
          data-testid="contact-headline"
          className="font-display text-[2.2rem] sm:text-5xl lg:text-6xl leading-[1.05] text-[var(--leo-ink)]"
        >
          Say <span className="said">hello</span>.
        </h1>
        <p className="mt-6 font-body text-lg sm:text-xl text-[var(--leo-ink-soft)] leading-relaxed">
          We're real humans who actually read our email. Whether you've got a
          question, some feedback, or just want to talk about Leo — we'd love to
          hear from you.
        </p>

        {/* Email CTA */}
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          data-testid="contact-email"
          className="btn btn--orange mt-8"
        >
          <Mail size={20} strokeWidth={2.25} />
          {CONTACT_EMAIL}
          <span aria-hidden>→</span>
        </a>
        <p className="mt-4 font-body text-sm text-[var(--leo-ink-soft)]">
          We'll get back to you within 48 hours.
        </p>
      </section>

      {/* Cards */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 grid gap-5 sm:gap-6 sm:grid-cols-3">
        {cards.map((c, i) => (
          <article
            key={c.title}
            className="sticker-soft bg-[var(--leo-paper)] p-6 text-left"
          >
            <div
              aria-hidden
              className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: accents[i % accents.length] }}
            >
              <span className="font-display font-bold">♥</span>
            </div>
            <h2 className="font-display text-lg sm:text-xl text-[var(--leo-ink)] leading-tight">
              {c.title}
            </h2>
            <p className="mt-2 font-body text-sm sm:text-base text-[var(--leo-ink-soft)] leading-relaxed">
              {c.body}
            </p>
          </article>
        ))}
      </section>

      {/* Other ways */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24">
        <article className="sticker bg-[var(--leo-cream-deep)] p-6 sm:p-8 text-center">
          <p className="font-hand text-3xl text-[var(--leo-ink)] mb-2">
            elsewhere
          </p>
          <h2 className="font-display text-2xl sm:text-3xl text-[var(--leo-ink)] leading-tight">
            Find us around the web
          </h2>
          <div className="mt-6 flex items-center justify-center gap-3">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              aria-label="Email Leo"
              className="w-11 h-11 rounded-xl bg-[var(--leo-paper)] flex items-center justify-center text-[var(--leo-ink)] transition-transform hover:-translate-y-0.5 hover:rotate-[-4deg]"
            >
              <Mail size={18} strokeWidth={2.25} />
            </a>
            <a
              href="https://instagram.com/leo.ai"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Leo on Instagram"
              className="w-11 h-11 rounded-xl bg-[var(--leo-paper)] flex items-center justify-center text-[var(--leo-ink)] transition-transform hover:-translate-y-0.5 hover:rotate-[-4deg]"
            >
              <Instagram size={18} strokeWidth={2.25} />
            </a>
          </div>
          <p className="mt-6 font-body text-xs text-[var(--leo-ink-soft)]">
            Operated by Code Ninja Coaching Ltd · Made in England.
          </p>
        </article>
      </section>

      <Footer />
    </main>
  );
}
